import { useState } from 'react';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge } from '@/ui/StatusBadge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { FormFieldWrapper } from '@/ui/FormField';
import { Input } from '@/ui/base/input';
import { Switch } from '@/ui/base/switch';
import { Label } from '@/ui/base/label';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { Spinner } from '@/ui/Spinner';
import { EmptyState } from '@/ui/EmptyState';
import { usePortalNav, type PortalNavItem } from '@/lib/usePortalNav';
import { avisarAcaoIndisponivel } from '@/modulos/admin/portal-publico/avisoIndisponivel';
import { Navigation, Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/ui/base/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { useEffect } from 'react';

const AREAS = [
  { value: 'menu_principal', label: 'Menu Principal' },
  { value: 'footer_servicos', label: 'Footer — Serviços' },
  { value: 'footer_institucional', label: 'Footer — Institucional' },
  { value: 'gov_topbar', label: 'Barra Gov' },
];

function NavDialog({
  open,
  onOpenChange,
  item,
  area: _area,
  parentOptions,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: PortalNavItem | null;
  area: string;
  parentOptions: PortalNavItem[];
}) {
  const [form, setForm] = useState({
    label: '',
    url: '',
    descricao: '',
    parent_id: '',
    externo: false,
    ordem: 0,
    ativo: true,
  });

  useEffect(() => {
    if (open) {
      if (item) {
        setForm({
          label: item.label,
          url: item.url || '',
          descricao: item.descricao || '',
          parent_id: item.parent_id || '',
          externo: item.externo,
          ordem: item.ordem,
          ativo: item.ativo,
        });
      } else {
        setForm({
          label: '',
          url: '',
          descricao: '',
          parent_id: '',
          externo: false,
          ordem: 0,
          ativo: true,
        });
      }
    }
  }, [item, open]);

  const handleSave = () => {
    avisarAcaoIndisponivel();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{item ? 'Editar item' : 'Novo item'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <FormFieldWrapper label="Label" required>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </FormFieldWrapper>
          <FormFieldWrapper label="URL" hint="Deixe vazio para items dropdown (pai)">
            <Input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="/teg/..."
            />
          </FormFieldWrapper>
          <FormFieldWrapper label="Descrição" hint="Texto auxiliar (submenus)">
            <Input
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            />
          </FormFieldWrapper>
          {parentOptions.length > 0 && (
            <FormFieldWrapper label="Item pai (dropdown)">
              <Select
                value={form.parent_id || 'none'}
                onValueChange={(v) => setForm({ ...form, parent_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Nenhum (raiz)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum (raiz)</SelectItem>
                  {parentOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormFieldWrapper>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <FormFieldWrapper label="Ordem">
              <Input
                type="number"
                value={form.ordem}
                onChange={(e) => setForm({ ...form, ordem: Number(e.target.value) })}
              />
            </FormFieldWrapper>
            <div className="space-y-3 pt-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.externo}
                  onCheckedChange={(v) => setForm({ ...form, externo: v })}
                />
                <Label className="text-sm">Link externo</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.ativo}
                  onCheckedChange={(v) => setForm({ ...form, ativo: v })}
                />
                <Label className="text-sm">Ativo</Label>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PortalNavegacaoPage() {
  const [activeArea, setActiveArea] = useState('menu_principal');
  const { data: items, isLoading } = usePortalNav(activeArea);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PortalNavItem | null>(null);
  const parentOptions = (items || []).filter((i) => !i.parent_id && !i.url);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Navegação do Portal"
        description="Gerencie o menu principal, rodapé e barra institucional do portal TEG."
      />

      <Tabs value={activeArea} onValueChange={setActiveArea}>
        <TabsList>
          {AREAS.map((a) => (
            <TabsTrigger key={a.value} value={a.value}>
              {a.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {AREAS.map((a) => (
          <TabsContent key={a.value} value={a.value}>
            <div className="flex justify-end mb-3">
              <Button
                size="sm"
                onClick={() => {
                  setEditingItem(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Novo item
              </Button>
            </div>
            {isLoading ? (
              <Spinner />
            ) : !items?.length ? (
              <EmptyState
                title="Nenhum item"
                description="Adicione itens de navegação."
                icon={Navigation}
              />
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Ordem</TableHead>
                          <TableHead>Label</TableHead>
                          <TableHead>URL</TableHead>
                          <TableHead>Pai</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="w-20" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((r) => (
                          <TableRow
                            key={r.id}
                            className="cursor-pointer"
                            onClick={() => {
                              setEditingItem(r);
                              setDialogOpen(true);
                            }}
                          >
                            <TableCell>{r.ordem}</TableCell>
                            <TableCell className="font-medium">{r.label}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {r.url ? (
                                <span className="flex items-center gap-1">
                                  {r.url} {r.externo && <ExternalLink className="h-3 w-3" />}
                                </span>
                              ) : (
                                <span className="italic opacity-50">Dropdown</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              {r.parent_id
                                ? items.find((i) => i.id === r.parent_id)?.label || '—'
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={r.ativo ? 'ativo' : 'inativo'} />
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingItem(r);
                                    setDialogOpen(true);
                                  }}
                                  aria-label="Editar"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <ConfirmDialog
                                  trigger={
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-destructive"
                                      onClick={(e) => e.stopPropagation()}
                                      aria-label="Excluir"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  }
                                  title="Excluir item?"
                                  description={`"${r.label}" será removido.`}
                                  variant="destructive"
                                  onConfirm={() => avisarAcaoIndisponivel()}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>
      <NavDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        item={editingItem}
        area={activeArea}
        parentOptions={parentOptions}
      />
    </div>
  );
}
